import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import type {
  ActionReference,
  RecordListOutputDescription,
} from "@smartsuite/automation-engine-api";
import { translateTrigger } from "../../../../../locales/i18n";
import {
  findInputAndPick,
  pickValueConfigLimitSort,
  pickValueConfigString,
} from "../../../../../utils/inputs";
import {
  ActionLabel,
  FIND_RECORD_LIMIT_ID,
  INPUT_ACTION_NAME_ID,
  INPUT_APPLICATION_ID,
  RECORD_CREATED_TRIGGER_IDS,
  TriggerLabel,
} from "../../../../../utils/constants";

export interface RecordsSourceOption {
  [key: string]: unknown;
  value: string;
  type: "action" | "trigger" | "no-actions" | "add-new-find";
  label: string;
  application?: {
    id: string;
    name: string;
  };
  actionReference?: ActionReference;
  triggerId?: string;
  /** What the chosen source declares about the records it hands over, copied
   * onto the consuming action's record list config. */
  recordListOutput?: RecordListOutputDescription;
  /** Only on the empty option: whether the consuming action takes a single
   * record, which decides why there is nothing to choose. */
}

const OUTPUT_REFERENCE_ACTION_IDS: string[] = [
  ActionLabel.sendWebhook,
  ActionLabel.ai,
  ActionLabel.createRecord,
  ActionLabel.updateRecord,
];

const OUTPUT_REFERENCE_TRIGGER_IDS: string[] = [
  TriggerLabel.webhook,
  ...RECORD_CREATED_TRIGGER_IDS,
];

export const isLoopingOutputAction = (action?: ActionCandidate): boolean =>
  isLoopingOutputActionId(action?.actionReference?.actionId);

export const isLoopingOutputActionId = (actionId?: string): boolean =>
  actionId !== undefined && OUTPUT_REFERENCE_ACTION_IDS.includes(actionId);

export const isLoopingOutputTrigger = (trigger?: TriggerCandidate): boolean =>
  trigger?.triggerReference?.triggerId !== undefined &&
  OUTPUT_REFERENCE_TRIGGER_IDS.includes(trigger.triggerReference.triggerId);

export const isSingleFindRecordsAction = (
  action?: ActionCandidate,
): boolean => {
  if (
    action?.actionReference?.actionId !== ActionLabel.findRecords ||
    action.inputs === undefined
  ) {
    return false;
  }

  const limitsInput = findInputAndPick(
    action.inputs,
    FIND_RECORD_LIMIT_ID,
    pickValueConfigLimitSort,
  );

  return limitsInput?.limit === 1;
};

const getActionOptionValue = (
  actionReference: ActionReference | undefined,
  sourceMode?: "recordList" | "outputReference",
): string =>
  `${actionReference?.actionId ?? ""}_${actionReference?.instanceId ?? ""}${
    sourceMode ? `_${sourceMode}` : ""
  }`;

export const webhookActionsToLoopingOptions = (
  actions: ActionCandidate[],
): RecordsSourceOption[] => {
  return actions.flatMap((action) => {
    const applicationId = findInputAndPick(
      action.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );

    const isWebhook =
      action.actionReference?.actionId === ActionLabel.sendWebhook;
    const isOutputReferenceAction = isLoopingOutputAction(action);

    if (isWebhook || isOutputReferenceAction) {
      const actionName = isWebhook
        ? action.description.info?.title
        : action.description.label;

      const applicationName = "";
      const baseOption = {
        type: "action" as const,
        label: actionName ?? "",
        application: isWebhook
          ? undefined
          : {
              id: applicationId ?? "",
              name: applicationName,
            },
        actionReference: action.actionReference,
      };

      return [
        {
          ...baseOption,
          value: getActionOptionValue(action.actionReference),
        },
      ];
    }

    const inputApplicationDescription = action.description.inputs.find(
      (input) => input.inputId === INPUT_APPLICATION_ID,
    );
    let applicationName = "";
    if (
      inputApplicationDescription !== undefined &&
      inputApplicationDescription.editor?.editor.oneofKind === "select"
    ) {
      const option =
        inputApplicationDescription.editor.editor.select.options.find(
          (option) => option.value === applicationId,
        );
      if (option !== undefined) {
        applicationName = option.label;
      }
    }
    const actionName = findInputAndPick(
      action.inputs,
      INPUT_ACTION_NAME_ID,
      pickValueConfigString,
    );

    return [
      {
        type: "action" as const,
        label: actionName ?? "",
        application: {
          id: applicationId ?? "",
          name: applicationName,
        },
        actionReference: action.actionReference,
        value: getActionOptionValue(action.actionReference),
      },
    ];
  });
};

export const webhookTriggerToLoopingOptions = (
  trigger: TriggerCandidate,
): RecordsSourceOption[] => {
  return [
    {
      value: `trigger`,
      type: "trigger",
      label: trigger.description.label ?? "",
      triggerId: trigger.description.triggerId,
    },
  ];
};

export const findRecordsActionsToOptions = (
  actions: ActionCandidate[],
): RecordsSourceOption[] => {
  return actions.map((action) => {
    const applicationId = findInputAndPick(
      action.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );
    const inputApplicationDescription = action.description.inputs.find(
      (input) => input.inputId === INPUT_APPLICATION_ID,
    );
    let applicationName = "";
    if (
      inputApplicationDescription !== undefined &&
      inputApplicationDescription.editor?.editor.oneofKind === "select"
    ) {
      const option =
        inputApplicationDescription.editor.editor.select.options.find(
          (option) => option.value === applicationId,
        );
      if (option !== undefined) {
        applicationName = option.label;
      }
    }
    const actionName = findInputAndPick(
      action.inputs,
      INPUT_ACTION_NAME_ID,
      pickValueConfigString,
    );
    return {
      type: "action",
      label: actionName ?? "",
      application: {
        id: applicationId ?? "",
        name: applicationName,
      },
      actionReference: action.actionReference,
      recordListOutput: action.description.recordListOutput,
      value: `${action.actionReference?.actionId ?? ""}_${
        action.actionReference?.instanceId ?? ""
      }`,
    };
  });
};

export const triggerToOption = (
  trigger: TriggerCandidate,
  currentAction: ActionCandidate,
): RecordsSourceOption | undefined => {
  if (
    trigger.description.recordListOutput === undefined ||
    currentAction.description.recordListInput === undefined ||
    !currentAction.description.recordListInput.requiredSourceTypeIds.includes(
      trigger.description.recordListOutput.sourceTypeId,
    )
  ) {
    return undefined;
  }
  const applicationId = findInputAndPick(
    trigger.inputs,
    INPUT_APPLICATION_ID,
    pickValueConfigString,
  );

  const inputApplicationDescription = trigger.description.inputs.find(
    (input) => input.inputId === INPUT_APPLICATION_ID,
  );
  let applicationName = "";
  if (
    inputApplicationDescription !== undefined &&
    inputApplicationDescription.editor?.editor.oneofKind === "select"
  ) {
    const option =
      inputApplicationDescription.editor.editor.select.options.find(
        (option) => option.value === applicationId,
      );
    if (option !== undefined) {
      applicationName = option.label;
    }
  }
  return {
    type: "trigger",
    label: translateTrigger(trigger.description),
    application: {
      id: applicationId ?? "",
      name: applicationName,
    },
    triggerId: trigger.description.triggerId,
    recordListOutput: trigger.description.recordListOutput,
    value: "trigger",
  };
};

export const getEmptyOption = (): RecordsSourceOption => {
  return {
    type: "no-actions",
    label: "no-actions",
    value: "no-actions",
  };
};

export const getAddNewOption = (): RecordsSourceOption => ({
  type: "add-new-find",
  label: "add-new-find",
  application: {
    id: "",
    name: "",
  },
  value: "add-new-find",
});
